import React, { useState, useEffect, useMemo } from "react";
import Button from "../../components/common/ui/Button";
import {
  PasswordInput,
  SelectInput,
  TextInput,
} from "../../components/common/ui/Inputs";
import { useDispatch, useSelector } from "react-redux";
import formatPhoneNumber from "./../../_helpers/formatPhoneNumber";
import { managementUserAction } from "./../../_store/managementUser.slice";
import { masterActions } from "./../../_store/master.slice";
import managementUserSchema from "../../validation/managementUserSchema";
import RadioButtonGroup from "../../components/common/ui/Inputs/RadioInput";
import { decryptData, encryptData } from "../../utils/encryptDecrypt";
import { useLocation } from "react-router-dom";
import { publicUserActions } from "../../_store";
import { UserRoleType } from "../../_helpers/enumerator";
import CustomLoader from "../../components/common/ui/Loader";
const ManagementUserForm = ({
  errorMessage,
  setErrorMessage,
  setShowExtraFeild, // Toggle visibility for extra fields (PDS Permission, )
  showExtraFeild,
  isEdit,
}) => {
  // Extracting values from Redux store: roles, user info, and other required data

  const {
    Role, // Role array fetched from API, used to populate role options in a dropdown menu for selection
    managementUserInfo,
    managementUserData,
    menu: menuList,
    createUserResponse,
    initialMenu,
    UserAccessibleRequestForm,
    PostalMailGrid,
  } = useSelector((state) => state.managementUser);

  const { Status } = useSelector((state) => state.dropdownOptions); // Dropdown options for status

  const { stateDrpList: stateDrplist } = useSelector((x) => x.master); // State dropdown list

  const { commonFacilityCode: FacilityID } = useSelector((state) => state.auth);

  const dispatch = useDispatch(); // Initializing Redux dispatch function
  const [isLoading, setIsLoading] = useState(false);
  // Getting ManagementUserID from the location (used for editing existing users)

  const location = useLocation();

  // Destructure `id` from location state if available; used to retrieve `ManagementUserID` from the list page
  // When a table row is clicked (for editing), `ManagementUserID` is passed from the list page.
  // However, if "Add User" is clicked, `id` will be undefined since no ID is provided for a new user.

  const { id: ManagementUserID } = location.state || {};

  // Copying the current user data from the Redux store

  let newUser = { ...managementUserData };

  useEffect(() => {
    // Dispatch action to fetch the list of user roles from the API, typically used to populate a role dropdown
    // dispatch(managementUserAction.GetUserRole());

    // Dispatch action to fetch the list of states, used to populate the state dropdown in the form
    // dispatch(masterActions.GetStateList());
  }, [dispatch]); // Dependencies include only dispatch to ensure actions run once on component mount

  // useEffect hook to filter menu items based on the user's role ID (UserRoleID) and populate permissions
  useEffect(() => {
    // If newUser has a defined UserRoleID (for edit mode), proceed with filtering
    if (newUser.UserRoleID) {
      // Filter menu items to include only those active and allowed for the selected role
      const filteredList = menuList.filter((menu) => {
        // Exclude inactive menus
        if (menu.isActive !== 1) return false;

        // Define role-based access permissions for each menu item
        const allowedRoles = {
          "Management Users": [12],
          Dashboard: [2, 3, 12, 13],
          Documents: [2, 12],
          "Postal Mails": [2, 3, 6, 12, 13],
          Lockdown: [2, 12],
          Inmates: [2, 6, 12, 13],
          "Public Users": [2, 6, 12],
          "Staff Requests": [2, 3, 6, 12, 13],
          Messages: [2, 3, 6, 12, 13],
          Photos: [2, 3, 6, 12, 13],
          Keywords: [2, 6, 12],
          Notices: [2, 3, 12, 13],
          "Connection Requests": [2, 3, 6, 12, 13],
          Reporting: [2, 12],
        };

        // Check if the selected UserRoleID is allowed for the current menu item
        return allowedRoles[menu.MenuName]?.includes(newUser.UserRoleID);
      });

      // Populate permissions for the filtered list
      populateMenuPermissions(filteredList);
    }
  }, [newUser.UserRoleID]); // Dependency array triggers on UserRoleID change

  // Function to populate menu permissions based on the filtered menu list
  // * Populates menu permissions for a user based on the provided filtered menu list.
  // * This function maps each main menu item and its submenus to a permissions structure
  // * that can be dispatched to the Redux store or used as parameters for API calls.

  const populateMenuPermissions = (filteredMenuList) => {
    // Generate a permissions array where each main menu item and its submenus are converted into structured permission objects
    const permissions = filteredMenuList
      .map((menu) => {
        // Main permission object for each menu item, handling special permissions logic based on user role and menu type
        const mainPermission = {
          managementMenuID: menu.ManagementMenuID,
          menuActiveStatus:
            // Conditional logic to dynamically enable or disable menu items based on user role and menu type.
            // If the user's role ID is 13 (a restricted role) and the menu item is "Inmates",
            // set `menuActiveStatus` to 0 to disable the menu item for this role.
            // For all other cases, retain the current status of `menu.MenuActiveStatus`.
            newUser.UserRoleID === 13 && menu.MenuName === "Inmates"
              ? 1 // Disable the "Inmates" menu for restricted role ID 13
              : menu.MenuActiveStatus, // Otherwise, use the existing menu status to enable or disable it
          subManagementMenuID: menu.SubManagementMenuID || 0,
          subMenuActiveStatus: menu.SubMenuActiveStatus || 0,
        };

        const subMenuPermissions = (menu.subMenu || []).map((subMenu) => ({
          managementMenuID: menu.ManagementMenuID,
          menuActiveStatus: menu.MenuActiveStatus,
          subManagementMenuID: subMenu.SubManagementMenuID,
          subMenuActiveStatus:
            newUser.UserRoleID === 13 && menu.MenuName === "Inmates"
              ? 0
              : // : menu.SubMenuActiveStatus,
              subMenu.SubMenuActiveStatus,
        }));

        // Combine main permission and submenu permissions into a single array for each menu item
        return [mainPermission, ...subMenuPermissions];
      })
      .flat(); // Flatten nested arrays into a single-level array of permission objects

    // Create a new user object with updated permissions and dispatch it to the Redux store
    const updatedUser = {
      ...managementUserData,
      type_ManagementUserMenuPermission: permissions,
    };

    // Dispatch action to update the Redux store with the new permissions
    dispatch(managementUserAction.setmanagementUserData(updatedUser));
  };

  // Fetching dropdown options for roles and states when the component mounts

  useEffect(() => {
    // Ensure the ManagementUserID is present
    if (ManagementUserID) {
      // Dispatch the action to fetch the user's information
      setIsLoading(true);
      dispatch(
        managementUserAction.GetManagementUserInfo(ManagementUserID)
      ).then((response) => {
        // Once the user's info is fetched successfully
        const managementUserInfo = response?.payload;

        if (managementUserInfo && Object.keys(managementUserInfo).length > 0) {
          // Prepare params based on managementUserInfo data
          let param = {
            ...managementUserData, // Spread the existing managementUserData to preserve other values
            ManagementUserID: managementUserInfo?.ManagementUserID,
            FacilityID: managementUserInfo?.FacilityID,
            UserRoleID: managementUserInfo?.UserRoleID,
            FirstName: managementUserInfo?.FirstName,
            MiddleName: managementUserInfo?.MiddleName,
            LastName: managementUserInfo?.LastName,
            Username: managementUserInfo?.UserName,
            UserPassword: managementUserInfo?.UserPassword
              ? decryptData(managementUserInfo?.UserPassword)
              : "", // Decrypting password before showing it in the form
            // UserPassword: decryptData("3FC07E79E8C404CC1E4C4E6CC19BCF12"), // Decrypting password before showing it in the form
            ActiveStatus: managementUserInfo?.ActiveStatus,
            Email: managementUserInfo?.Email,
            Address1: managementUserInfo?.Address1,
            City: managementUserInfo?.City,
            StateNameID: managementUserInfo?.StateNameID,
            ZIP: managementUserInfo?.ZIP,
            PhoneNumber: managementUserInfo?.PhoneNumber,
            isPublicDefenderServicesAccess:
              managementUserInfo?.IsPublicDefenderServicesAccess,
            isAllowtoAccessOthersPDForm:
              managementUserInfo?.IsAllowtoAccessOthersPDForm === "False"
                ? 0
                : 1,
          };

          if (
            managementUserInfo?.UserRoleID === UserRoleType.PublicDefender ||
            managementUserInfo?.UserRoleID === UserRoleType.PublicDefenderAdmin
          ) {
            setShowExtraFeild(true); // Show extra fields if Role is 10 or 11
          } else {
            setShowExtraFeild(false); // Hide extra fields for other Role values
          }

          // Dispatching action to set the user data into the local Redux store state.
          dispatch(managementUserAction.setmanagementUserData(param));
        }
        setIsLoading(false);
      });
    }
  }, [dispatch, ManagementUserID]);

  // This effect is specifically triggered when the "edit" action is performed,
  // which fetches the user data (`managementUserInfo`) from the backend via the `GetManagementUserInfo` action.
  // Once the user data (`managementUserInfo`) is available after clicking "edit",
  // the form fields are populated with the retrieved user data.
  // The effect runs after the user data is fetched and stored in `managementUserInfo` in Redux.
  // Then, the form data is updated by dispatching the `setmanagementUserData` action to store it locally.

  const [showPassword, setShowPassword] = useState(false); // State to toggle password visibility

  // Toggle password visibility

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  // Handling input change for text and number form fields
  const handleChange = (e) => {
    const { name, value } = e.target; // Destructure name and value from the input element

    switch (name) {
      case "PhoneNumber":
        // Format the phone number
        newUser[name] = formatPhoneNumber(value);
        break;

      case "ZIP":
        // Ensure only digits are allowed for ZIP field
        newUser[name] = value.replace(/\D/g, "");
        break;

      case "Email":
        // Directly update the Email field
        newUser[name] = value;
        break;
      case "Username":
        // Username field doesn't accept special characters or spaces between letters
        newUser[name] = value.replace(/[^a-zA-Z0-9]/g, "");
        break;
      case "City":
        // City field doesn't accept special characters or spaces between letters or numbers
        newUser[name] = value.replace(/[^a-zA-Z]/g, "");
        break;
      case "FirstName":
      case "MiddleName":
      case "LastName":
      case "Address1":
        // All other fields: Remove special characters but accept spaces
        newUser[name] = value.replace(/[^a-zA-Z0-9\s]/g, "");
        break;
      case "UserPassword":
        if (value.includes("'")) return;
        else newUser[name] = value;
        if (value.includes("`")) return;
        else newUser[name] = value;
        break;
      default:
        // All other fields: Remove special characters but accept spaces
        newUser[name] = value;
        break;
    }
    setErrorMessage((prev) => ({
      ...prev,
      [name]: "", // Clear the specific field error
    }));

    // After any input field changes, dispatch the updated data to the Redux store
    dispatch(managementUserAction.setmanagementUserData(newUser)); // This will update the form data in the local Redux store
  };

  const handleBlur = async (e) => {
    let { name, value } = e.target;
    try {
      // Clear error if validation passes
      setErrorMessage((prev) => ({
        ...prev,
        [name]: "", // Clear the specific field error
      }));
      // Validate the field using the Joi schema and handle both required and other validation
      await managementUserSchema.validateAsync(
        { [name]: value },
        { abortEarly: false }
      );
    } catch (err) {
      // Find the error that matches the current field name
      const fieldError = err.details.find((error) => error.path.includes(name));

      if (fieldError) {
        setErrorMessage((prev) => ({
          ...prev,
          [name]: fieldError.message, // Set the specific field error from Joi
        }));
      }
    }
  };

  // Handling changes for dropdowns (select fields)

  const handleSelectChange = async (selectedOption, name) => {
    // Clear any existing error messages for the dropdown field being changed
    // setErrorMessage({});
    if (name === "UserRoleID") {
      dispatch(managementUserAction.resetMenuList());
      let updatedRequestForm = [...UserAccessibleRequestForm];
      if (updatedRequestForm.length > 0) {
        updatedRequestForm = updatedRequestForm.map((requestForm) => {
          return {
            ...requestForm,
            ActiveStatus: 0,
            FormView: 0,
            FormRespond: 0,
          };
        });
        setErrorMessage((prev) => ({
          ...prev,
          [name]: "", // Clear the specific field error
        }));
        dispatch(managementUserAction.setRequestForm(updatedRequestForm));
      }
      ResetPostMailList();
    }
    const escapedName = name.replace(/\./g, "\\."); // Escape if the name contains dots
    const element = document.querySelector(`[name=${escapedName}]`);
    if (element) {
      element.classList.remove("input-error");
    }

    // Clear the error message for the specific field
    setErrorMessage((prevErrors) => {
      const { [name]: _, ...remainingErrors } = prevErrors; // Remove the specific error by destructuring
      return remainingErrors; // Return the remaining errors
    });

    // let chk = [...initialMenu];
    // Handle select changes specifically for Role and State dropdowns
    if (name === "UserRoleID" || name === "StateNameID") {
      newUser[name] = selectedOption === null ? 0 : selectedOption?.value; // Get the selected value and assign it to the newUser object

      // Conditionally show or hide extra fields based on the selected Role value
      if (
        name === "UserRoleID" &&
        (selectedOption?.value === UserRoleType.PublicDefender ||
          selectedOption?.value === UserRoleType.PublicDefenderAdmin)
      ) {
        setShowExtraFeild(true); // Show extra fields if Role is 10 or 11
      } else if (selectedOption?.value === UserRoleType.FacilityStaffSAO) {
        let updatedMenuList = [...initialMenu];
        updatedMenuList = menuList.map((menu) => {
          if (menu.MenuName === "Inmates") {
            return {
              ...menu,
              MenuActiveStatus: 1,
              subMenu: menu.subMenu?.map((subMenuItem) => ({
                ...subMenuItem,
                SubMenuActiveStatus: 0, // Set SubMenuActiveStatus to 0
              })),
            };
          } else if (menu.MenuName === "Connection Requests") {
            return {
              ...menu,
              MenuActiveStatus: 0,
              subMenu: menu.subMenu?.map((subMenuItem) => ({
                ...subMenuItem,
                SubMenuActiveStatus: 0, // Set SubMenuActiveStatus to 0
              })),
            };
          } else {
            return {
              ...menu,
              subMenu: menu.subMenu?.map((subMenuItem) => ({
                ...subMenuItem,
                SubMenuActiveStatus: 1, // Set SubMenuActiveStatus to 0
              })),
            };
          }
        });

        // const param = filteredMenuList.map(())
        dispatch(managementUserAction.updatemenuList(updatedMenuList));
      } else {
        if (name !== "StateNameID") {
          setShowExtraFeild(false); // Hide extra fields for other Role values
        }
      }
    } else if (
      name === "ActiveStatus" ||
      name === "isPublicDefenderServicesAccess" ||
      name === "isAllowtoAccessOthersPDForm"
    ) {
      // For the Status field, convert the selected option to an integer before storing
      newUser[name] = parseInt(selectedOption);
    }

    try {
      await managementUserSchema.validateAsync(
        { [name]: selectedOption?.value },
        { abortEarly: false }
      );

      // Clear error if validation passes
      setErrorMessage((prev) => ({
        ...prev,
        [name]: "", // Clear the specific field error
      }));
    } catch (err) {
      // Find the error that matches the current field name
      const fieldError = err.details.find((error) => error.path.includes(name));

      if (fieldError) {
        setErrorMessage((prev) => ({
          ...prev,
          [name]: fieldError.message, // Set the specific field error from Joi
        }));
      }
    }
    // After the dropdown value changes, dispatch the updated data to the Redux store
    dispatch(managementUserAction.setmanagementUserData(newUser)); // This will update the form data in the local Redux store
  };

  const ResetPostMailList = () => {
    let UpdatedPostMail = [...PostalMailGrid];

    if (UpdatedPostMail.length > 0) {
      UpdatedPostMail = UpdatedPostMail.map((postmail) => {
        return {
          ...postmail,
          IsActive: 0,
        };
      });
      dispatch(managementUserAction.setPostalMailGrid(UpdatedPostMail));
    }
  };

  if (isLoading) {
    return <CustomLoader />;
  }

  return (
    <div>
      <form autoComplete="off">
        {/* First Row: Username, Last Name, First Name */}
        <div className="row ">
          <div className="col-sm-6 col-md-6 col-lg-4">
            <div className="form-floating mb-3">
              <SelectInput
                options={Role}
                value={newUser?.UserRoleID}
                handleChange={(selectedOption) =>
                  handleSelectChange(selectedOption, "UserRoleID")
                }
                isMulti={false}
                closeMenuOnSelect={true}
                isDisabled={
                  isEdit &&
                  (managementUserInfo?.UserRoleID ===
                    UserRoleType.PublicDefender ||
                    managementUserInfo?.UserRoleID ===
                    UserRoleType.PublicDefenderAdmin)
                }
                isLoading={false}
                isClearable={managementUserData?.UserRoleID !== 0}
                required={true}
                placeholder="-- Select Role --"
                label="Role"
                name="UserRoleID"
                errorMessage={errorMessage}
                handleBlur={
                  () =>
                    handleBlur({
                      target: {
                        name: "UserRoleID",
                        value: newUser?.UserRoleID,
                      },
                    }) // Pass value properly
                }
              />
            </div>
          </div>
          <div className="col-sm-6 col-md-6 col-lg-4">
            <div className="form-floating mb-3">
              <TextInput
                name="FirstName"
                id="FirstName"
                label="First Name"
                value={newUser.FirstName}
                handleChange={handleChange}
                placeholder="First Name"
                required={true}
                errorMessage={errorMessage}
                maxLength={50}
                handleBlur={handleBlur}
              ></TextInput>
            </div>
          </div>
          <div className="col-sm-6 col-md-6 col-lg-4">
            <div className="form-floating mb-3">
              <TextInput
                name="MiddleName"
                id="MiddleName"
                label="Middle Name"
                value={newUser.MiddleName}
                handleChange={handleChange}
                placeholder="Middle Name"
                required={false}
                //   errorMessage={errorMessage}
                maxLength={50}
              ></TextInput>
            </div>
          </div>
          <div className="col-sm-6 col-md-6 col-lg-4">
            <div className="form-floating mb-3">
              <TextInput
                name="LastName"
                id="LastName"
                label="Last Name"
                value={newUser.LastName}
                handleChange={handleChange}
                placeholder="Last Name"
                required={true}
                errorMessage={errorMessage}
                maxLength={50}
                handleBlur={handleBlur}
              ></TextInput>
            </div>
          </div>

          <div className="col-sm-6 col-md-6 col-lg-4">
            <div className="form-floating mb-3">
              <TextInput
                name="Username"
                id="Username"
                label="Username"
                value={newUser.Username}
                handleChange={handleChange}
                placeholder="Username"
                required={true}
                errorMessage={errorMessage}
                maxLength={50}
                handleBlur={handleBlur}
                autoComplete="new-username"
              ></TextInput>
            </div>
          </div>

          <div className="col-sm-6 col-md-6 col-lg-4">
            <div className="mb-3">
              <PasswordInput
                name="UserPassword"
                id="Password"
                label="Password"
                value={newUser.UserPassword}
                // type="Password"
                handleChange={handleChange}
                showPassword={showPassword}
                togglePasswordVisibility={togglePasswordVisibility}
                placeholder="Password"
                required={true}
                errorMessage={errorMessage}
                maxlength={25}
                handleBlur={handleBlur}
                autoComplete="new-password"
              />
            </div>
          </div>

          <div className="col-sm-6 col-md-6 col-lg-4">
            <div className="form-floating mb-3">
              <TextInput
                name="Email"
                id="Email"
                label="E-mail"
                value={newUser.Email}
                handleChange={handleChange}
                placeholder="E-mail"
                required={false}
                errorMessage={errorMessage}
                maxLength={75}
                handleBlur={handleBlur}
              ></TextInput>
            </div>
          </div>

          <div className="col-sm-6 col-md-6 col-lg-4">
            <div className="form-floating mb-3">
              <TextInput
                name="Address1"
                id="Address1"
                label="Address"
                value={newUser.Address1}
                handleChange={handleChange}
                placeholder="Address"
                required={false}
                //   errorMessage={errorMessage}
                maxLength={50}
              ></TextInput>
            </div>
          </div>

          {/* Second Row: Email, State, City */}

          <div className="col-sm-6 col-md-6 col-lg-4">
            <div className="form-floating mb-3">
              <TextInput
                name="City"
                id="City"
                label="City"
                value={newUser.City}
                handleChange={handleChange}
                placeholder="City"
                required={false}
                //   errorMessage={errorMessage}
                maxLength={25}
              ></TextInput>
            </div>
          </div>

          <div className="col-sm-6 col-md-6 col-lg-4">
            <div className="form-floating mb-3">
              <SelectInput
                options={stateDrplist}
                value={newUser.StateNameID ? newUser.StateNameID : 0}
                handleChange={(selectedOption) =>
                  handleSelectChange(selectedOption, "StateNameID")
                }
                isMulti={false}
                closeMenuOnSelect={true}
                isDisabled={false}
                isLoading={false}
                isClearable={newUser.StateNameID !== 0}
                placeholder="-- Select State --"
                label="State"
              />
            </div>
          </div>

          <div className="col-sm-6 col-md-6 col-lg-4">
            <div className="form-floating mb-3">
              <TextInput
                type="text"
                name="ZIP"
                id="ZIP"
                label="ZIP"
                value={newUser.ZIP}
                handleChange={handleChange}
                placeholder="ZIP"
                required={false}
                maxLength={5}
                handleBlur={handleBlur}
                errorMessage={errorMessage}
              ></TextInput>
            </div>
          </div>

          <div className="col-sm-6 col-md-6 col-lg-4">
            <div className="form-floating mb-3">
              <TextInput
                name="PhoneNumber"
                id="PhoneNumber"
                label="Phone Number"
                value={newUser.PhoneNumber}
                handleChange={handleChange}
                placeholder="Phone Number"
                required={false}
                // maxLength={5}
                errorMessage={errorMessage}
                handleBlur={handleBlur}
              ></TextInput>
            </div>
          </div>
          <div className="col-sm-6 col-md-6 col-lg-4">
            <div className="form-floating mb-3">
              <RadioButtonGroup
                name="ActiveStatus"
                id="ActiveStatus"
                label="Status"
                selectedValue={newUser.ActiveStatus} // Default to "2" if Status is not set
                handleChange={(e) =>
                  handleSelectChange(e.target.value, "ActiveStatus")
                }
                options={Status}
                // required={true} // Set to true if the field is required
                disabled={false} // Set to true if you want to disable the radio buttons
              />
            </div>
          </div>
          {showExtraFeild && (
            <>
              <div className="col-sm-6 col-md-6 col-lg-4">
                <div className="form-floating mb-3">
                  <RadioButtonGroup
                    name="isPublicDefenderServicesAccess"
                    id="isPublicDefenderServicesAccess"
                    label="PDS Permission"
                    selectedValue={newUser.isPublicDefenderServicesAccess} // Default to "2" if Status is not set
                    handleChange={(e) =>
                      handleSelectChange(
                        e.target.value,
                        "isPublicDefenderServicesAccess"
                      )
                    }
                    options={Status}
                    // required={true} // Set to true if the field is required
                    disabled={false} // Set to true if you want to disable the radio buttons
                  />
                </div>
              </div>
              <div className="col-sm-6 col-md-6 col-lg-4">
                <div className="form-floating mb-3">
                  <RadioButtonGroup
                    name="isAllowtoAccessOthersPDForm"
                    id="isAllowtoAccessOthersPDForm"
                    label="All Forms Access"
                    selectedValue={newUser.isAllowtoAccessOthersPDForm} // Default to "2" if Status is not set
                    handleChange={(e) =>
                      handleSelectChange(
                        e.target.value,
                        "isAllowtoAccessOthersPDForm"
                      )
                    }
                    options={Status}
                    // required={true} // Set to true if the field is required
                    disabled={false} // Set to true if you want to disable the radio buttons
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default React.memo(ManagementUserForm);
